// PlanBadgeV2 — cụm "gói đang dùng" trên thanh trên cùng (sếp Phú 15/08/2026).
//
// Vì sao đưa lên header: gói + số chỗ còn lại trước đây chỉ xem được ở /marketplace và
// /billing. Người quản trị không nhớ mình đang ở bậc nào, tới lúc mời người thứ N+1 mới
// biết hết chỗ. Đưa lên thanh trên cùng thì trạng thái đó luôn nằm trong tầm mắt.
//
// Dữ liệu: `/users/seat-status` — endpoint NHẸ (chỉ đọc license + đếm member), KHÔNG phải
// `/marketplace/apps` (endpoint đó còn gọi cnv-cdn + tổng đài, không được để thanh trên
// cùng kéo theo mỗi lần đổi trang). Dùng chung `queryKey: ['seat-status']` với BillingPage
// và InvitationsPage ⇒ mở các trang đó không phát sinh request thứ hai.
//
// ⚠️ `plan_label` phải là GÓI NỀN. Trước 15/08 nó lấy license paid đầu tiên theo hạn nên
// tenant có add-on ra tên "Kích hoạt CNV Call" — dán lên header là sai ở MỌI trang. Đã sửa
// tận gốc trong licenseService (ADR-036), đây chỉ hiển thị.
//
// Style ở admin/src/styles/topbar-plan.css — CSS riêng của SaaS, KHÔNG bỏ vào
// cnv-ui/styles/ui-v2.css (file đó Core cũng nạp, Core không có cụm này).
//
// Tên gói lấy từ bảng của APP (src/marketplace/planCopy) — chung một bảng với trang Kho
// ứng dụng, để header và trang đó không bao giờ gọi cùng một gói bằng 2 tên.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Briefcase, AlertTriangle } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { isUnlimitedSeats } from '@cnv/ui/utils/seatQuota';
import { planApi } from '../../lib/inboxClient';
import { usePlanName } from '../../marketplace/planCopy';

export default function PlanBadgeV2() {
  const { t } = useLingui();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['seat-status'],
    queryFn: () => planApi.seatStatus(),
    // Gói đổi theo đợt kích hoạt bên Core, không đổi từng phút. 5 phút là đủ tươi mà không
    // bắn request mỗi lần đổi trang.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Tên gói lấy từ nguồn chung với Kho ứng dụng (kernel planCopy) — dùng `plan_label` thô của BE
  // thì header ghi "CNV Work On Cloud · All-in-one" còn /marketplace ghi "On-Cloud All-in-one",
  // hai tên cho một gói cách nhau đúng một màn hình.
  // ⚠️ Hook PHẢI gọi TRƯỚC nhánh return sớm bên dưới (Rules of Hooks — lint:hooks chặn).
  const planName = usePlanName(data?.plan_code, data?.plan_label);

  // Chưa có dữ liệu thì KHÔNG dựng khung rỗng — thanh trên cùng nhảy layout mỗi lần load
  // còn khó chịu hơn là hiện muộn nửa giây.
  if (!data) return null;

  const quota = Number(data.quota) || 0;
  const used = Number(data.active_count) || 0;
  const unlimited = isUnlimitedSeats(data);   // dùng thử 14 ngày — không giới hạn user (19/10/2026)
  // Hết chỗ (hoặc đã có người bị khoá vì hết chỗ) = thứ admin cần thấy NGAY, tô cảnh báo.
  const warn = !!data.over || Number(data.locked_count) > 0 || (!unlimited && quota > 0 && used >= quota);

  const label = planName || t`Chưa kích hoạt`;

  return (
    <button
      type="button"
      className={`v2-plan${warn ? ' v2-plan--warn' : ''}`}
      // Màu icon theo BẬC, trùng bảng màu Console /plans (0 xám · 1 xanh · 2 hổ phách) để
      // nhìn một cái biết đang ở đâu trên thang mà không phải đọc chữ.
      data-tier={data.plan_tier_level ?? ''}
      onClick={() => navigate('/marketplace')}
      title={t`Xem kho ứng dụng và thang nâng cấp`}
    >
      {/* TÊN GÓI chỉ hiện khi workspace CHƯA có gói. Từ 21/08 cụm đổi workspace bên TRÁI
          thanh đã dán viên nhãn tên gói ngay cạnh tên workspace ⇒ để nguyên ở đây là một
          thanh 38px ghi "On-Cloud All-in-one" hai lần cách nhau một gang tay (sếp Phú bắt).
          Nhưng KHÔNG bỏ hẳn: khi chưa kích hoạt thì viên nhãn bên trái tự ẩn, câu "Chưa
          kích hoạt" chỉ còn chỗ này để nói — bỏ luôn là mất tín hiệu, không phải bớt lặp. */}
      {data.plan_code ? null : (
        <span className="v2-plan__name">
          <Briefcase size={14} aria-hidden="true" />
          {label}
        </span>
      )}

      <span className="v2-plan__stats">
        <span className="v2-plan__stat">
          <i><Trans>Người dùng</Trans></i>
          <b>{unlimited ? `${used}/∞` : `${used}/${quota}`}</b>
        </span>
        <span className="v2-plan__stat">
          <i><Trans>Hạn gói</Trans></i>
          {/* Hạn của GÓI NỀN, KHÔNG phải đợt hết sớm nhất: workspace còn đợt KM/add-on ngắn hơn
              thì `next_expiry` là hạn của đợt đó — khách mua gói tới 12/2027 mà thanh này ghi
              10/2026 rồi tưởng mình vẫn đang dùng thử (sếp Phú bắt 27.08.2026). */}
          <b>{(() => {
            const exp = data.plan_expiry ?? data.next_expiry;
            return exp ? format(new Date(exp), 'dd/MM/yyyy') : t`Không có hạn`;
          })()}</b>
        </span>
        {warn && <AlertTriangle size={14} className="v2-plan__warn-icon" aria-hidden="true" />}
      </span>
    </button>
  );
}
